import { Bars, DeltaCount, EmptyState, Legend } from '@/ui'
import { formatDay } from '@/lib/time'
import { cn } from '@/lib/utils'
import { TrendingUp } from 'lucide-react'
import { bucketLabel, fmtNum, isHourBucket, shortDay } from './format'

/** One bucket of a series. The funnel fields are optional so the list's small
 *  bars can share the type with the full chart. */
export interface DayPoint {
  day: string
  sessions: number
  users: number
  paywall_shown?: number
  purchase_started?: number
  purchase_success?: number
}

/** The sessions of each bucket as short bars, no axis. Sized in pixels rather
 *  than flex so every row's bars line up column to column. The newest bar is
 *  solid, the rest are lighter. */
export function SessionBars({ series, width = 104, height = 26 }: { series: Pick<DayPoint, 'day' | 'sessions'>[]; width?: number; height?: number }) {
  if (series.length === 0) return <span style={{ width }} className="inline-block shrink-0" />
  const max = Math.max(1, ...series.map((d) => d.sessions))
  const last = series[series.length - 1]
  const lines = series.slice(-14).map((d) => `${bucketLabel(d.day)}: 세션 ${fmtNum(d.sessions)}`)
  const unit = isHourBucket(series[0].day) ? '시간' : '일'
  const title = `${lines.join('\n')}${series.length > 14 ? `\n최근 14${unit}만 보인다` : ''}`
  return (
    <span title={title} className="flex shrink-0 items-end gap-px" style={{ width, height }}>
      {series.map((d, i) => (
        <span
          key={d.day}
          className={cn('min-w-[2px] flex-1 rounded-[1px]', i === series.length - 1 && last.sessions > 0 ? 'bg-chart-1' : 'bg-chart-1/40')}
          style={{ height: d.sessions > 0 ? `${Math.max(8, (d.sessions / max) * 100)}%` : '0%' }}
        />
      ))}
    </span>
  )
}

/** The last finished day's sessions against the day before it. Buckets are
 *  UTC days, so the last one is usually today and still filling up — set
 *  against a whole day it reads as a drop every morning ("▼20 (100%)" at
 *  09:30 KST). When the last bucket is the current UTC day it is left out
 *  and the line says which day it speaks for. */
export function DayDelta({ series, now = new Date() }: { series: DayPoint[]; now?: Date }) {
  let end = series.length - 1
  const partial = end >= 0 && series[end].day === now.toISOString().slice(0, 10)
  if (partial) end -= 1
  if (end < 1) return null
  const today = series[end].sessions
  const prev = series[end - 1].sessions
  if (prev === 0 && today === 0) return null
  const pct = prev > 0 ? Math.round((Math.abs(today - prev) / prev) * 100) : null
  return (
    <span
      className="inline-flex flex-wrap items-baseline gap-x-1.5 text-meta text-muted-foreground"
      title={partial ? '오늘(UTC)은 아직 끝나지 않아 끝난 날끼리 비교한다' : undefined}
    >
      {today === prev ? <span>전일과 같다</span> : <DeltaCount now={today} before={prev} />}
      {pct !== null && today !== prev && <span className="tabular-nums">({pct}%)</span>}
      {today !== prev && <span>전일 대비 세션</span>}
      {partial && <span className="tabular-nums">· {formatDay(series[end].day)} 기준</span>}
    </span>
  )
}

function sum(xs: (number | undefined)[]): number {
  return xs.reduce<number>((n, x) => n + (x ?? 0), 0)
}

/** The sessions/users chart and, when there is anything to show, the
 *  paywall/purchase chart under it. Columns, not a line: a count per discrete
 *  bucket, and a line would imply values in between that do not exist.
 *  Silent buckets are drawn as zero rather than skipped, because the server
 *  zero-fills the window — a chart that omits silent days shows a trend that
 *  never happened. */
export function UsageTrend({
  series,
  onPick,
  selected = null,
}: {
  series: DayPoint[]
  /** Makes the columns pickable: a day → its hours, an hour → everything below. */
  onPick?: (i: number) => void
  selected?: number | null
}) {
  if (series.length === 0) {
    return <EmptyState icon={TrendingUp} title="추이가 아직 없다" description="기간을 바꾸거나 수집기가 다음에 가져올 때까지 기다린다." />
  }
  const days = series.map((d) => d.day)
  const anyActivity = series.some((d) => d.sessions > 0 || d.users > 0)
  const anyFunnel = series.some((d) => (d.paywall_shown ?? 0) > 0)
  if (!anyActivity) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="이 기간에 세션이 없다"
        description={`${days[0]} → ${days[days.length - 1]} (UTC)가 비어 있다. 쓴 사람이 없거나 수집기가 그 날을 아직 가져오지 않았다.`}
      />
    )
  }
  const hint = onPick ? '\n눌러서 아래 전체를 이 구간으로 좁힌다' : ''
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Legend
          items={[
            { label: '세션', swatch: 'bg-chart-1/40', value: sum(series.map((d) => d.sessions)) },
            { label: '사용자', swatch: 'bg-chart-2' },
          ]}
        />
        <Bars
          label="세션과 사용자"
          labels={days}
          outer={series.map((d) => d.sessions)}
          inner={series.map((d) => d.users)}
          outerFill="bg-chart-1/40"
          innerFill="bg-chart-2"
          tick={shortDay}
          title={(i) => `${bucketLabel(series[i].day)}\n세션 ${fmtNum(series[i].sessions)}\n사용자 ${fmtNum(series[i].users)}${hint}`}
          onPick={onPick}
          selected={selected}
        />
      </div>
      {anyFunnel && (
        <div className="space-y-2 border-t border-border pt-4">
          <Legend
            items={[
              { label: '페이월 노출', swatch: 'bg-chart-4/50 dark:bg-chart-4/80', value: sum(series.map((d) => d.paywall_shown)) },
              { label: '구매', swatch: 'bg-chart-3', value: sum(series.map((d) => d.purchase_success)) },
            ]}
          />
          <Bars
            label="페이월 노출과 구매"
            labels={days}
            outer={series.map((d) => d.paywall_shown ?? 0)}
            inner={series.map((d) => d.purchase_success ?? 0)}
            outerFill="bg-chart-4/50 dark:bg-chart-4/80"
            innerFill="bg-chart-3"
            tick={shortDay}
            height={80}
            title={(i) =>
              `${bucketLabel(series[i].day)}\n페이월 노출 ${fmtNum(series[i].paywall_shown ?? 0)}\n구매 시작 ${fmtNum(series[i].purchase_started ?? 0)}\n구매 ${fmtNum(series[i].purchase_success ?? 0)}`
            }
          />
        </div>
      )}
    </div>
  )
}
